import { useEffect, useRef, type RefObject } from 'react';

/**
 * Two-finger pinch on `ref` changes `zoom` (a percent) instead of the page.
 * The page itself never pinch-zooms (touch-action on <html>, plus the iOS
 * gesture block in main.tsx), so this is the only zoom a touch user gets,
 * and only on work areas that opt in: grids and text sheets.
 * Pass no `onZoomChange` to leave pinch doing nothing on the element.
 */
export function usePinchZoom(
  ref: RefObject<HTMLElement | null>,
  zoom: number,
  onZoomChange: ((zoom: number) => void) | undefined,
  min = 50,
  max = 150,
): void {
  const latest = useRef({ zoom, onZoomChange });
  latest.current = { zoom, onZoomChange };
  const enabled = Boolean(onZoomChange);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let start: { distance: number; zoom: number } | null = null;
    let pending: number | null = null;
    let frame = 0;
    const distance = (e: TouchEvent) => Math.hypot(e.touches[0]!.clientX - e.touches[1]!.clientX, e.touches[0]!.clientY - e.touches[1]!.clientY);

    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) start = { distance: distance(e) || 1, zoom: latest.current.zoom };
    };
    const onMove = (e: TouchEvent) => {
      if (!start || e.touches.length !== 2) return;
      e.preventDefault();
      pending = Math.min(max, Math.max(min, Math.round(start.zoom * distance(e) / start.distance)));
      // One render per frame: the grid is too heavy to re-render on every touchmove.
      if (!frame) frame = requestAnimationFrame(() => {
        frame = 0;
        if (pending !== null && pending !== latest.current.zoom) latest.current.onZoomChange?.(pending);
      });
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) start = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [ref, enabled, min, max]);
}
